import React, { useState } from "react";
import { Product } from "./ProductInterface";
import { cloneProduct, putProduct } from "../../services/products/productAxios";
import { enqueueSnackbar } from "notistack";
import { formatCurrency, getCurrencySymbol } from "../../utils/formatters";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import { IconDuplicate } from "../../assets/icons/icons";
import { handleApi } from "../../utils/handleApi";
import toast from "react-hot-toast";
import Loader from "../common/Loader";
import { ProductAction, ProductState } from "../../reducers/productPage/ProductPageReducer";

interface ProductsTableProps {
  productsState: ProductState;
  dispatchProducts: (action: ProductAction) => void;
}

export default function ProductsTable({ productsState, dispatchProducts }: ProductsTableProps) {
  const { language, country } = useAuth();
  const currencySymbol = getCurrencySymbol(country);
  const [loadingCloneProducts, setLoadingCloneProducts] = useState<Set<string>>(new Set());

  const startEditing = (product: Product) => {
    dispatchProducts({ type: "START_EDITING", product: product });
  };

  const handleInputChange = (productId: string, field: 'name' | 'price', value: string) => {
    if (field === 'price') value = value === '' ? '0' : value.replace(/^0+(?=\d)/, '');
    dispatchProducts({ type: "UPDATE_FIELD", productId: productId, field: field, value: value });
  };

  const handleClone = async (productId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setLoadingCloneProducts(prev => new Set(prev).add(productId));
    const { res, error } = await handleApi(cloneProduct(productId));
    if ( error ) {
      console.error(error);
      setLoadingCloneProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
      return;
    }
    const newProduct = res;
    productsState.products.unshift(newProduct);
    dispatchProducts({ type: "SET_PRODUCTS", payload: productsState.products });
    toast.success(dictionary["product-cloned-successfully"][language]);
    setLoadingCloneProducts(prev => {
      const newSet = new Set(prev);
      newSet.delete(productId);
      return newSet;
    });
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-sm font-medium text-gray-500 w-1/2">
                {dictionary["product"][language]}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-sm font-medium text-gray-500 w-1/4">
                {dictionary["price"][language]}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-sm font-medium text-gray-500 w-1/4">
                {dictionary["last-updated"][language]}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-sm font-medium text-gray-500 w-1/4">
                {dictionary["duplicate"][language]}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {productsState.products.map((product) => {
              const isEditing = productsState.editingIds.has(product.id);
              const editValues = isEditing ? productsState.editValuesMap[product.id] : null;
              
              return (
                <tr 
                  key={product?.id || ""} 
                  className={`hover:bg-gray-50 cursor-pointer ${isEditing ? 'bg-blue-50' : ''}`}
                  onClick={() => !isEditing && startEditing(product)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <img 
                        src={(product && product.images && product.images.length > 0) ? product.images[0] : ''} 
                        alt={product.name} 
                        className="w-10 h-10 rounded-md flex-shrink-0" 
                      />
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValues?.name}
                          onChange={(e) => handleInputChange(product.id, "name", e.target.value)}
                          className="border rounded px-2 py-1 w-full"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-900 line-clamp-2">{product.name}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editValues?.price}
                        onChange={(e) => handleInputChange(product.id, "price", e.target.value)}
                        className="border rounded px-2 py-1 w-full"
                        step="0.01"
                      />
                    ) : (
                      formatCurrency(Number(product.price), currencySymbol)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {product.updated_at}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <button onClick={(e) => handleClone(product.id, e)} disabled={loadingCloneProducts.has(product.id)}>
                      {loadingCloneProducts.has(product.id) ? <IconDuplicate color="#cdcdcd" /> : <IconDuplicate />}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
